/**
 * Media Session — kilit ekranı / medya bildirimi entegrasyonu.
 *
 * ## Neden?
 *
 * Müzik çalarken kullanıcı telefonun kilit ekranından veya masaüstünün medya
 * bildiriminden parçayı duraklatabilmeli, sıradakine geçebilmeli. Tarayıcı
 * `navigator.mediaSession` ile bunu destekler; yalnız `<video>`/`<audio>`
 * oynatmak yeterli değildir, metadata ve eylemler bağlanmalıdır.
 *
 * Desteklemeyen tarayıcılarda sessizce devre dışı kalır.
 */
import { useEffect, useRef } from 'react';
import type { Item } from './api.ts';
import { displayTitle } from './format.ts';
import { resizeProxyImage } from './imageSize.ts';
import { mediaUrl } from './mediaToken.ts';

export interface UseMediaSessionOptions {
  /** Çalan öğe; yoksa metadata temizlenir. */
  item: Item | null;
  /** Gerçek medya öğesi (`<audio>` veya `<video>`); hazır olmadan `null`. */
  video: HTMLMediaElement | null;
  /** "Sonraki parça" eylemi (varsa). */
  onNext?: (() => void) | undefined;
}

const SEEK_STEP_SECONDS = 10;

export function useMediaSession({ item, video, onNext }: UseMediaSessionOptions): void {
  /**
   * `onNext` her render'da yeni bir fonksiyon olabiliyor; etkisiyle birlikte
   * bağımlılık yapmak metadata'yı gereksiz yere yeniden kurardı. Ref'e alıp
   * ana etkiyi yalnız öğe/medya değişiminde çalıştırıyoruz.
   */
  const onNextRef = useRef(onNext);
  useEffect(() => {
    onNextRef.current = onNext;
  }, [onNext]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    const session = navigator.mediaSession;

    if (item === null) {
      session.metadata = null;
      return;
    }

    if (typeof MediaMetadata !== 'undefined') {
      session.metadata = new MediaMetadata({
        title: displayTitle(item),
        artist: item.music?.albumArtist ?? item.music?.artist ?? 'pbfork',
        album: item.music?.album ?? '',
        artwork: [
          {
            src:
              item.albumCoverUrl ??
              resizeProxyImage(item.posterUrl, 'w500') ??
              item.thumbnailUrl ??
              mediaUrl(`/api/items/${item.id}/thumbnail`),
            sizes: '500x500',
            type: 'image/jpeg',
          },
        ],
      });
    }

    const handlers: Array<[MediaSessionAction, MediaSessionActionHandler]> = [
      [
        'play',
        () => {
          void video?.play().catch(() => undefined);
        },
      ],
      ['pause', () => video?.pause()],
      ['stop', () => video?.pause()],
      [
        'seekbackward',
        (details) => {
          if (video) {
            video.currentTime = Math.max(
              0,
              video.currentTime - (details.seekOffset ?? SEEK_STEP_SECONDS),
            );
          }
        },
      ],
      [
        'seekforward',
        (details) => {
          if (video) {
            video.currentTime += details.seekOffset ?? SEEK_STEP_SECONDS;
          }
        },
      ],
      ['nexttrack', () => onNextRef.current?.()],
    ];

    for (const [action, handler] of handlers) {
      try {
        session.setActionHandler(action, handler);
      } catch {
        // Tarayıcı bu eylemi desteklemiyor; diğerleri çalışmaya devam eder.
      }
    }

    return () => {
      for (const [action] of handlers) {
        try {
          session.setActionHandler(action, null);
        } catch {
          // Desteklenmeyen eylem; yok sayılır.
        }
      }
    };
  }, [item, video]);
}
